import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { MessageSquare, X, Send, User, Loader2, ArrowLeft, Search, Stethoscope, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { playMessageSound } from '../utils/notificationSound';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessageBell = ({ userId, iconColor = '#333', currentUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [inbox, setInbox] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('inbox'); // 'inbox', 'contacts', 'chat'
  
  const dropdownRef = useRef(null);
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/unread-count`, { headers });
      const newCount = res.data.count || 0;
      // Play sound if new messages arrived
      if (newCount > unreadCount && unreadCount >= 0) {
        playMessageSound();
      }
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  }, [unreadCount]);

  // Load unread count
  useEffect(() => {
    if (userId) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [userId, loadUnreadCount]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showChat) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showChat]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadInbox = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages/inbox`, { headers });
      setInbox(res.data?.messages || []);
    } catch (error) {
      console.error('Error loading inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const res = await axios.get(`${API}/messages/contacts`, { headers });
      setContacts(res.data.contacts || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const loadConversation = async (contactId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages/conversation/${contactId}`, { headers });
      setMessages(res.data.messages || []);
      await axios.post(`${API}/messages/mark-read/${contactId}`, {}, { headers }).catch(() => {});
      loadUnreadCount();
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, {
        recipient_id: selectedContact.id,
        content: newMessage.trim()
      }, { headers });
      
      setNewMessage('');
      loadConversation(selectedContact.id);
      toast.success('Messaggio inviato');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setSending(false);
    }
  };

  const openDropdown = () => {
    setIsOpen(true);
    setView('inbox');
    loadInbox();
    loadContacts();
  };

  const openChatModal = () => {
    setIsOpen(false);
    setShowChat(true);
    setView('inbox');
    loadInbox();
    loadContacts();
  };

  const selectContact = (contact) => {
    setSelectedContact(contact);
    setView('chat');
    loadConversation(contact.id);
  };

  const getContactIcon = (contact) => {
    if (contact.role === 'admin') return <Users className="w-4 h-4" />;
    if (contact.role === 'doctor') return <Stethoscope className="w-4 h-4" />;
    return <User className="w-4 h-4" />;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return 'Ora';
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  const filteredContacts = contacts.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Render the chat content (used in both dropdown and modal)
  const renderChatContent = (inModal = false) => (
    <div className={`flex flex-col ${inModal ? 'h-full' : 'max-h-[400px]'}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center justify-between bg-gradient-to-r from-[#2A9FCB] to-[#1e7a9c]">
        <div className="flex items-center gap-2">
          {view !== 'inbox' && (
            <button 
              onClick={() => { setView('inbox'); setSelectedContact(null); }}
              className="p-1 rounded hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
          )}
          <MessageSquare className="w-5 h-5 text-white" />
          <h3 className="font-bold text-white">
            {view === 'chat' && selectedContact ? selectedContact.name : 'Messaggi'}
          </h3>
          {unreadCount > 0 && view === 'inbox' && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {unreadCount} nuovi
            </span>
          )}
        </div>
        {inModal && (
          <button 
            onClick={() => setShowChat(false)}
            className="p-1 rounded hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-[#2A9FCB] animate-spin" />
          </div>
        ) : view === 'inbox' ? (
          <>
            {/* Search & New Message */}
            <div className="p-3 border-b space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca contatti..."
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]/30 focus:border-[#2A9FCB]"
                />
              </div>
              <button
                onClick={() => setView('contacts')}
                className="w-full py-2 text-sm font-medium text-[#2A9FCB] hover:bg-[#2A9FCB]/10 rounded-lg transition-colors"
              >
                + Nuovo Messaggio
              </button>
            </div>

            {/* Inbox Messages */}
            {inbox.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessun messaggio</p>
              </div>
            ) : (
              <div className="divide-y">
                {inbox.map((msg) => (
                  <div
                    key={msg.id}
                    onClick={() => selectContact({ id: msg.sender_id, name: msg.sender_name })}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !msg.read ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2A9FCB] to-[#1e7a9c] flex items-center justify-center text-white">
                        <User className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`text-sm ${!msg.read ? 'font-semibold' : 'font-medium'} truncate`}>
                            {msg.sender_name}
                          </p>
                          <span className="text-xs text-gray-400">{formatTime(msg.created_at)}</span>
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{msg.content}</p>
                      </div>
                      {!msg.read && (
                        <div className="w-2.5 h-2.5 bg-[#2A9FCB] rounded-full flex-shrink-0 mt-1.5"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : view === 'contacts' ? (
          <>
            {/* Contact List */}
            <div className="p-3 border-b">
              <p className="text-xs text-gray-500 mb-2">Seleziona un destinatario:</p>
            </div>
            <div className="divide-y">
              {filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  onClick={() => selectContact(contact)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2A9FCB] to-[#1e7a9c] flex items-center justify-center text-white">
                    {getContactIcon(contact)}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{contact.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{contact.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Chat View */
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
              {messages.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">Inizia una conversazione</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMe = msg.sender_id === userId;
                  return (
                    <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        isMe 
                          ? 'bg-gradient-to-r from-[#2A9FCB] to-[#1e7a9c] text-white' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isMe ? 'text-white/70' : 'text-gray-400'}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Message Input (only in chat view) */}
      {view === 'chat' && (
        <div className="p-3 border-t bg-gray-50">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Scrivi un messaggio..."
              className="flex-1 px-4 py-2 border rounded-full text-sm focus:ring-2 focus:ring-[#2A9FCB]/30 focus:border-[#2A9FCB]"
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              className="p-2 bg-gradient-to-r from-[#2A9FCB] to-[#1e7a9c] text-white rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Message Button */}
      <button
        onClick={openDropdown}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="message-bell-btn"
      >
        <MessageSquare className="w-6 h-6" style={{ color: iconColor }} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown / Modal - Portal */}
      {isOpen && ReactDOM.createPortal(
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black/40 z-[9998]" onClick={() => setIsOpen(false)} />
          {/* Panel */}
          <div className="fixed inset-4 sm:inset-auto sm:top-16 sm:right-4 sm:left-auto sm:w-96 sm:max-h-[80vh] bg-white rounded-2xl shadow-2xl border z-[9999] flex flex-col overflow-hidden">
            {/* Header with close button */}
            <div className="px-4 py-3 border-b flex items-center justify-between shrink-0" style={{ background: '#F9FAFB' }}>
              <h3 className="font-bold text-lg" style={{ color: '#2A9FCB' }}>Messaggi</h3>
              <button onClick={() => setIsOpen(false)} className="p-2 rounded-full hover:bg-gray-200 transition-colors" data-testid="close-messages-btn">
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {renderChatContent(false)}
            </div>
          
            {/* Footer - Open Full Modal */}
            <div className="px-4 py-3 border-t text-center bg-gray-50 shrink-0">
              <button 
                onClick={openChatModal}
                className="text-xs font-medium text-[#2A9FCB] hover:underline"
                data-testid="open-full-chat-btn"
              >
                Apri chat completa
              </button>
            </div>
          </div>
        </>
      , document.body)}

      {/* Full Chat Modal */}
      {showChat && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
          onClick={() => setShowChat(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg h-[600px] max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="full-chat-modal"
          >
            {renderChatContent(true)}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MessageBell;
